/**
 * Dynamic cart cross-sells via Shopify Product Recommendations (Search & Discovery).
 */
class CartCrossSell extends HTMLElement {
  static INIT_DELAY = 40;
  static _drawerSlideCache = [];
  static _drawerSlideCacheKey = '';
  static _mergedSlidesCache = { key: '', slides: [] };
  static _mergedSlidesPromise = null;

  constructor() {
    super();
    this.abortController = null;
    this.cartUpdateUnsubscriber = undefined;
    this.mutationObserver = undefined;
    this._loadToken = 0;
  }

  connectedCallback() {
    const isPage = this.dataset.layout === 'page';
    this.limit = parseInt(this.dataset.limit, 10) || (isPage ? 12 : 12);
    if (isPage) this.limit = Math.max(this.limit, 10);
    this.perSourceLimit = parseInt(this.dataset.perSourceLimit, 10) || (isPage ? 10 : 6);
    this.sectionId = this.dataset.sectionId || 'cart-cross-sell-page';
    this.recommendationsBase = this.dataset.url || '/recommendations/products';
    this.headingEl = this.querySelector('.cart-upsell__heading');
    if (this.dataset.heading && this.headingEl) {
      this.headingEl.textContent = this.dataset.heading;
    }

    this.parseProductIds(this.dataset.productIds);
    this.bindAddToCart();
    this.observeDrawerUpdates();

    if (this.isDrawerLayout()) {
      this.bootstrapDrawer();
    } else {
      this.load();
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        if (this.isCartPageLayout()) {
          this.handleCartUpdatePage();
        } else if (!this._drawerBootstrapPending) {
          requestAnimationFrame(() => {
            setTimeout(() => this.handleCartUpdateDrawer(), 120);
          });
        }
      });
    }
  }

  disconnectedCallback() {
    if (this.isCartPageLayout()) {
      this.abortController?.abort();
    }
    this.cartUpdateUnsubscriber?.();
    this.mutationObserver?.disconnect();
  }

  isCartPageLayout() {
    return this.dataset.layout === 'page';
  }

  isDrawerLayout() {
    return !this.isCartPageLayout();
  }

  getRecommendationSectionId() {
    return 'cart-cross-sell-page';
  }

  buildRecommendationUrl(productId, limit, intent) {
    const url = new URL(this.recommendationsBase, window.location.origin);
    url.searchParams.set('section_id', this.getRecommendationSectionId());
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', String(limit));
    if (intent) {
      url.searchParams.set('intent', intent);
    }
    return `${url.pathname}${url.search}`;
  }

  parseProductIds(idsString) {
    this.productIds = (idsString || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean)
      .map((id) => String(id));
    this.excludedProductIds = new Set(this.productIds);
  }

  isProductExcluded(productId) {
    if (productId == null || productId === '') return false;
    return this.excludedProductIds.has(String(productId));
  }

  syncExcludedFromCart(cart) {
    const ids = CartCrossSell.cartProductIds(cart);
    this.parseProductIds(ids.join(','));
    this.dataset.productIds = ids.join(',');
    if (cart?.items?.length) {
      this._drawerRecommendProductId = String(cart.items[cart.items.length - 1].product_id);
    }
    return ids;
  }

  /** Same sources as cart page — all cart line products for diverse recommendations. */
  getRecommendationSourceIds() {
    return this.productIds;
  }

  static cartProductIds(cart) {
    const ids = [];
    const seen = new Set();
    (cart?.items || []).forEach((item) => {
      const id = String(item.product_id);
      if (!seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    });
    return ids;
  }

  static cartIdsChanged(prevIds, newIds) {
    const prev = new Set(prevIds);
    return newIds.length !== prev.size || newIds.some((id) => !prev.has(id));
  }

  static removeProductFromDrawer(productId) {
    const el = document.querySelector('cart-drawer cart-cross-sell');
    if (!el || !productId) return;
    el.removeAddedProduct(productId);
  }

  removeAddedProduct(productId) {
    if (!productId) return;
    this.excludedProductIds.add(String(productId));
    this.querySelectorAll(`.swiper-slide[data-product-id="${productId}"]`).forEach((slide) => {
      slide.remove();
    });
    if (this.isDrawerLayout()) {
      this.filterSlidesInCartDrawer();
    } else {
      this.filterSlidesInCartPage();
    }
  }

  async handleCartUpdatePage() {
    try {
      const cart = await fetch(`${window.routes?.cart_url || '/cart'}.js`).then((r) => r.json());
      if (!cart.items?.length) {
        this.clearAndHide();
        return;
      }

      const newIds = CartCrossSell.cartProductIds(cart);
      const idsChanged = CartCrossSell.cartIdsChanged(this.productIds, newIds);

      this.parseProductIds(newIds.join(','));
      this.dataset.productIds = newIds.join(',');

      if (idsChanged) {
        CartCrossSell.clearMergedSlidesCache();
        await this.load();
      } else {
        this.filterSlidesInCartPage();
      }
    } catch (e) {
      console.error(e);
    }
  }

  async handleCartUpdateDrawer() {
    try {
      const cart = await fetch(`${window.routes?.cart_url || '/cart'}.js`).then((r) => r.json());
      await this.syncFromCartDrawer(cart);
    } catch (e) {
      console.error(e);
    }
  }

  async bootstrapDrawer() {
    this._drawerBootstrapPending = true;
    try {
      const cart = await fetch(`${window.routes?.cart_url || '/cart'}.js`).then((r) => r.json());
      if (!cart?.items?.length) {
        this.clearAndHide();
        return;
      }
      this.syncExcludedFromCart(cart);
      if (this.syncDrawerFromPageCrossSell()) {
        return;
      }
      if (this.restoreDrawerSlideCache()) {
        this.dataset.initialized = 'true';
        this.load().catch((e) => console.error(e));
        return;
      }
      await this.load();
    } catch (e) {
      console.error(e);
      await this.load();
    } finally {
      this._drawerBootstrapPending = false;
      const slideCount = this.querySelectorAll('.swiper-wrapper .swiper-slide').length;
      if (slideCount > 0) {
        this.dataset.initialized = 'true';
      } else {
        this.removeAttribute('data-initialized');
      }
    }
  }

  async syncFromCartDrawer(cart) {
    if (!cart?.items?.length) {
      this.clearAndHide();
      return;
    }

    const prevIds = [...this.productIds];
    this.syncExcludedFromCart(cart);

    const idsChanged = CartCrossSell.cartIdsChanged(prevIds, this.productIds);

    if (idsChanged) {
      CartCrossSell.clearMergedSlidesCache();
      await this.load();
    } else {
      this.filterSlidesInCartDrawer();
    }
  }

  filterSlidesInCartPage() {
    const wrapper = this.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    wrapper.querySelectorAll('.swiper-slide').forEach((slide) => {
      const id = slide.dataset.productId;
      if (id && this.isProductExcluded(id)) {
        slide.remove();
      }
    });

    if (!wrapper.querySelector('.swiper-slide')) {
      if (this.productIds.length) {
        this.load();
        return;
      }
      if (this.isCartPageLayout()) {
        this.clearAndHide();
      }
      return;
    }

    this.show();
    this.initSwiper();
  }

  filterSlidesInCartDrawer() {
    const wrapper = this.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    wrapper.querySelectorAll('.swiper-slide').forEach((slide) => {
      const id = slide.dataset.productId;
      if (id && this.isProductExcluded(id)) {
        slide.remove();
      }
    });

    if (!wrapper.querySelector('.swiper-slide')) {
      if (this.productIds.length) {
        this.load();
      }
      return;
    }

    this.show();
    this.initSwiper();
  }

  getPerSourceLimit() {
    return this.perSourceLimit;
  }

  async fetchRecommendationResponses(sourceIds, perSourceLimit, signal) {
    const cacheKey = sourceIds.join(',');
    if (CartCrossSell._mergedSlidesCache.key === cacheKey && CartCrossSell._mergedSlidesCache.slides.length) {
      return this.cloneSlidesForLayout(CartCrossSell._mergedSlidesCache.slides);
    }

    if (CartCrossSell._mergedSlidesPromise?.key === cacheKey) {
      const cached = await CartCrossSell._mergedSlidesPromise.promise;
      return this.cloneSlidesForLayout(cached);
    }

    const loadPromise = this.fetchRecommendationResponsesUncached(sourceIds, perSourceLimit, signal);
    CartCrossSell._mergedSlidesPromise = { key: cacheKey, promise: loadPromise };

    try {
      const slides = await loadPromise;
      if (slides.length) {
        CartCrossSell._mergedSlidesCache = {
          key: cacheKey,
          slides: slides.map((slide) => slide.cloneNode(true)),
        };
      }
      return this.cloneSlidesForLayout(slides);
    } finally {
      if (CartCrossSell._mergedSlidesPromise?.key === cacheKey) {
        CartCrossSell._mergedSlidesPromise = null;
      }
    }
  }

  cloneSlidesForLayout(slides) {
    return slides.map((slide) => {
      if (this.isDrawerLayout()) {
        if (slide.querySelector('.upsell-card')) {
          return slide.cloneNode(true);
        }
        return this.convertPageSlideToDrawerSlide(slide) || slide.cloneNode(true);
      }
      if (slide.querySelector('.cart-rec-card')) {
        return slide.cloneNode(true);
      }
      return slide.cloneNode(true);
    });
  }

  async fetchRecommendationResponsesUncached(sourceIds, perSourceLimit, signal) {
    const fetchWithIntent = (intent) =>
      Promise.all(
        sourceIds.map((productId) =>
          fetch(
            this.buildRecommendationUrl(productId, perSourceLimit, intent),
            signal ? { signal } : undefined
          ).then((r) => r.text())
        )
      );

    let responses = await fetchWithIntent();
    let slides = this.mergeRecommendationSlidesDiverse(responses);
    if (slides.length) return slides;

    responses = await fetchWithIntent('complementary');
    slides = this.mergeRecommendationSlidesDiverse(responses);
    if (slides.length) return slides;

    responses = await fetchWithIntent('related');
    return this.mergeRecommendationSlidesDiverse(responses);
  }

  static clearDrawerSlideCache() {
    CartCrossSell._drawerSlideCache = [];
    CartCrossSell._drawerSlideCacheKey = '';
    try {
      sessionStorage.removeItem('xlfeet:cart-cross-sell');
    } catch (e) {
      /* ignore */
    }
  }

  static clearMergedSlidesCache() {
    CartCrossSell._mergedSlidesCache = { key: '', slides: [] };
    CartCrossSell._mergedSlidesPromise = null;
  }

  static setDrawerSlideCache(cartIds, slides) {
    if (!slides?.length) return;
    const key = (cartIds || []).join(',');
    CartCrossSell._drawerSlideCacheKey = key;
    CartCrossSell._drawerSlideCache = slides.map((slide) => slide.cloneNode(true));
    try {
      sessionStorage.setItem(
        'xlfeet:cart-cross-sell',
        JSON.stringify({
          key,
          html: CartCrossSell._drawerSlideCache.map((slide) => slide.outerHTML),
        })
      );
    } catch (e) {
      /* ignore */
    }
  }

  static getDrawerSlideCache(cartIds) {
    const key = (cartIds || []).join(',');
    if (CartCrossSell._drawerSlideCacheKey === key && CartCrossSell._drawerSlideCache.length) {
      return CartCrossSell._drawerSlideCache.map((slide) => slide.cloneNode(true));
    }

    try {
      const raw = sessionStorage.getItem('xlfeet:cart-cross-sell');
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (parsed.key !== key || !Array.isArray(parsed.html)) return [];
      const wrap = document.createElement('div');
      return parsed.html
        .map((markup) => {
          wrap.innerHTML = markup;
          return wrap.firstElementChild;
        })
        .filter(Boolean)
        .map((slide) => slide.cloneNode(true));
    } catch (e) {
      return [];
    }
  }

  cacheSlidesForDrawer(slides) {
    if (!slides?.length) return;
    const drawerSlides = this.isDrawerLayout()
      ? slides
      : slides
          .map((slide) => this.convertPageSlideToDrawerSlide(slide))
          .filter(Boolean);
    if (drawerSlides.length) {
      CartCrossSell.setDrawerSlideCache(this.productIds, drawerSlides);
    }
  }

  syncDrawerFromPageCrossSell() {
    const pageEl = document.querySelector('cart-cross-sell[data-layout="page"]');
    if (!pageEl) return false;

    const pageSlides = pageEl.querySelectorAll('.swiper-wrapper .swiper-slide');
    if (!pageSlides.length) return false;

    const slides = [];
    pageSlides.forEach((pageSlide) => {
      if (this.isProductExcluded(pageSlide.dataset.productId)) return;
      const converted = this.convertPageSlideToDrawerSlide(pageSlide);
      if (converted) slides.push(converted);
    });

    if (!slides.length) return false;

    this.renderSlides(slides.slice(0, this.limit));
    CartCrossSell.setDrawerSlideCache(this.productIds, slides);
    this.show();
    this.initSwiper();
    this.dataset.initialized = 'true';
    return true;
  }

  restoreDrawerSlideCache() {
    const cached = CartCrossSell.getDrawerSlideCache(this.productIds)
      .filter((slide) => !this.isProductExcluded(slide.dataset.productId))
      .slice(0, this.limit);

    if (!cached.length) return false;

    this.renderSlides(cached);
    this.show();
    this.initSwiper();
    this.dataset.initialized = 'true';
    return true;
  }

  finishDrawerLoadAttempt() {
    if (this.syncDrawerFromPageCrossSell()) {
      return true;
    }
    if (this.restoreDrawerSlideCache()) {
      this.dataset.initialized = 'true';
      return true;
    }
    const applied = this.applyFallback();
    const slideCount = this.querySelectorAll('.swiper-wrapper .swiper-slide').length;
    if (slideCount > 0) {
      this.dataset.initialized = 'true';
      return true;
    }
    return applied;
  }

  async load() {
    if (!this.productIds.length) {
      if (this.isCartPageLayout()) {
        this.clearAndHide();
      }
      return;
    }

    if (this.isDrawerLayout()) {
      this._loadQueue = (this._loadQueue || Promise.resolve()).then(() => this.executeLoad());
      return this._loadQueue;
    }

    return this.executeLoad();
  }

  async executeLoad() {
    if (this.isDrawerLayout()) {
      return this.executeLoadDrawer();
    }

    this.abortController?.abort();
    this.abortController = new AbortController();
    return this.executeLoadWithSignal(this.abortController.signal);
  }

  async executeLoadDrawer() {
    if (this._drawerLoadPromise) {
      return this._drawerLoadPromise;
    }

    this._drawerLoadPromise = this.executeLoadWithSignal(null).finally(() => {
      this._drawerLoadPromise = null;
    });
    return this._drawerLoadPromise;
  }

  async executeLoadWithSignal(signal) {
    const loadToken = this.isDrawerLayout() ? ++this._loadToken : null;

    this.classList.add('is-loading');

    try {
      const perSourceLimit = this.getPerSourceLimit();
      const sourceIds = this.getRecommendationSourceIds();

      if (!sourceIds.length) {
        if (this.isCartPageLayout()) {
          this.clearAndHide();
        }
        return;
      }

      const responses = await this.fetchRecommendationResponses(sourceIds, perSourceLimit, signal);

      if (loadToken != null && loadToken !== this._loadToken) {
        return;
      }

      const slides = responses;

      if (slides.length) {
        this.renderSlides(slides);
        this.cacheSlidesForDrawer(slides);
        this.show();
        this.initSwiper();
        if (this.isDrawerLayout()) {
          this.dataset.initialized = 'true';
        }
      } else if (this.isCartPageLayout()) {
        this.applyFallback();
      } else if (!this.querySelector('.swiper-wrapper .swiper-slide')) {
        this.finishDrawerLoadAttempt();
      } else {
        this.show();
        this.initSwiper();
      }
    } catch (e) {
      if (loadToken != null && loadToken !== this._loadToken) return;
      if (e.name !== 'AbortError') {
        console.error(e);
      }
      if (this.isCartPageLayout()) {
        this.applyFallback();
      } else if (!this.querySelector('.swiper-wrapper .swiper-slide')) {
        this.finishDrawerLoadAttempt();
      } else {
        this.show();
        this.initSwiper();
      }
    } finally {
      this.classList.remove('is-loading');
      if (loadToken == null || loadToken === this._loadToken) {
        if (!this.querySelector('.swiper-wrapper .swiper-slide')) {
          if (this.isCartPageLayout()) {
            this.applyFallback();
          } else {
            this.finishDrawerLoadAttempt();
          }
        } else if (this.isDrawerLayout()) {
          this.show();
          this.initSwiper();
        }
      }
    }
  }

  static drawerNeedsCrossSellLoad(el) {
    if (!el) return false;
    return !el.querySelector('.swiper-wrapper .swiper-slide');
  }

  static async ensureDrawerCrossSell() {
    const el = document.querySelector('cart-drawer cart-cross-sell');
    if (!el) return;

    if (el._loadQueue) {
      try {
        await el._loadQueue;
      } catch (e) {
        /* ignore */
      }
    }

    if (!el.querySelector('.swiper-wrapper .swiper-slide')) {
      if (el.syncDrawerFromPageCrossSell()) return;
      if (el.restoreDrawerSlideCache()) return;
      await CartCrossSell.refreshDrawerCrossSell();
      return;
    }

    if (!el.querySelector('.cart-upsell__swiper.swiper-initialized')) {
      el.initSwiper();
    }
  }

  static async refreshDrawerCrossSell() {
    const el = document.querySelector('cart-drawer cart-cross-sell');
    if (!el) return;

    try {
      const cart = await fetch(`${window.routes?.cart_url || '/cart'}.js`).then((r) => r.json());
      if (!cart?.items?.length) {
        el.clearAndHide();
        return;
      }
      el.syncExcludedFromCart(cart);
      if (el.syncDrawerFromPageCrossSell()) return;
      await el.load();
    } catch (e) {
      console.error(e);
    }
  }

  static escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  convertPageSlideToDrawerSlide(pageSlide) {
    const productId = pageSlide.dataset.productId;
    const card = pageSlide.querySelector('.cart-rec-card');
    if (!productId || !card) return null;

    const titleEl = card.querySelector('.cart-rec-card__title');
    const title = titleEl?.textContent?.trim() || '';
    const url = titleEl?.getAttribute('href') || card.querySelector('a[href]')?.getAttribute('href') || '#';
    const img = card.querySelector('.cart-rec-card__img, .cart-rec-card__media img');
    const imgSrc =
      img?.getAttribute('src') ||
      img?.getAttribute('srcset')?.split(',')[0]?.trim()?.split(' ')[0] ||
      '';
    const imgAlt = img?.getAttribute('alt') || title;
    const priceText =
      card.querySelector('.cart-rec-card__price .price-item--last')?.textContent?.trim() ||
      card.querySelector('.cart-rec-card__price .price')?.textContent?.trim() ||
      card.querySelector('.cart-rec-card__price')?.textContent?.trim() ||
      '';

    const addBtn = card.querySelector('.cart-upsell__add[data-variant-id]');
    const variantId = addBtn?.getAttribute('data-variant-id');
    const viewLink = card.querySelector('a.cart-rec-card__btn--link');

    const slide = document.createElement('div');
    slide.className = 'swiper-slide';
    slide.dataset.productId = productId;

    const article = document.createElement('article');
    article.className = 'upsell-card';

    let actionHtml = '';
    if (variantId) {
      actionHtml = `<button type="button" class="button cart-upsell__add" data-variant-id="${CartCrossSell.escapeHtml(variantId)}">ADD</button>`;
    } else if (viewLink) {
      actionHtml = `<a href="${CartCrossSell.escapeHtml(viewLink.getAttribute('href') || url)}" class="button cart-upsell__add" aria-label="View ${CartCrossSell.escapeHtml(title)}">View</a>`;
    } else {
      actionHtml = '<button type="button" class="button cart-upsell__add" disabled>Sold out</button>';
    }

    article.innerHTML = `
      <a class="upsell-card__image" href="${CartCrossSell.escapeHtml(url)}">
        <img loading="lazy" alt="${CartCrossSell.escapeHtml(imgAlt)}" src="${CartCrossSell.escapeHtml(imgSrc)}" width="220" height="220">
      </a>
      <div class="upsell-card__body">
        <a class="upsell-card__title" href="${CartCrossSell.escapeHtml(url)}">${CartCrossSell.escapeHtml(title)}</a>
        <div class="upsell-card__price">${CartCrossSell.escapeHtml(priceText)}</div>
      </div>
      ${actionHtml}
    `;

    slide.appendChild(article);
    return slide;
  }

  getSlideCardSelector() {
    return this.isDrawerLayout() ? '.upsell-card, .cart-rec-card' : '.cart-rec-card';
  }

  normalizeSlideForLayout(slide) {
    if (!this.isDrawerLayout()) return slide;

    if (slide.querySelector('.upsell-card')) {
      return slide.cloneNode(true);
    }

    return this.convertPageSlideToDrawerSlide(slide);
  }

  parseSlidesFromHtml(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const slides = [];
    let root = doc.querySelector('.cart-cross-sell-api-response');
    if (!root) {
      root =
        doc.querySelector('.cart-cross-sell-page-section') ||
        doc.querySelector('.cart-cross-sell-section') ||
        doc.querySelector('.shopify-section');
    }
    if (!root) return slides;

    root.querySelectorAll('.swiper-slide').forEach((slide) => {
      const productId = slide.dataset.productId;
      if (!productId || this.isProductExcluded(productId)) return;
      if (!slide.querySelector('.cart-rec-card') && !slide.querySelector('.upsell-card')) return;
      slides.push(slide.cloneNode(true));
    });
    return slides;
  }

  mergeRecommendationSlidesDiverse(responseTexts) {
    const buckets = responseTexts.map((html) => this.parseSlidesFromHtml(html)).filter((b) => b.length);
    if (!buckets.length) return [];

    const seen = new Set();
    const merged = [];
    let round = 0;
    let stagnant = 0;

    const tryAddSlide = (slide) => {
      const productId = slide.dataset.productId;
      if (!productId || seen.has(productId)) return false;
      seen.add(productId);
      merged.push(slide);
      return true;
    };

    while (merged.length < this.limit && stagnant < buckets.length + 2) {
      let addedThisRound = false;

      for (let i = 0; i < buckets.length; i++) {
        if (merged.length >= this.limit) break;
        const slide = buckets[i][round];
        if (!slide) continue;
        if (tryAddSlide(slide)) addedThisRound = true;
      }

      round++;
      stagnant = addedThisRound ? 0 : stagnant + 1;
    }

    if (merged.length < this.limit) {
      for (const bucket of buckets) {
        for (const slide of bucket) {
          if (merged.length >= this.limit) break;
          tryAddSlide(slide);
        }
      }
    }

    return merged.slice(0, this.limit);
  }

  renderSlides(slides) {
    const wrapper = this.querySelector('.swiper-wrapper');
    if (!wrapper || !slides.length) return;

    const hadSlides = wrapper.querySelector('.swiper-slide');
    const fragment = document.createDocumentFragment();
    slides.forEach((slide) => fragment.appendChild(slide));

    if (hadSlides) {
      this.destroySwiper();
    }
    wrapper.replaceChildren(fragment);
    wrapper.style.alignItems = 'stretch';

    if (this.isCartPageLayout()) {
      this.cacheSlidesForDrawer(slides);
    } else if (this.isDrawerLayout()) {
      CartCrossSell.setDrawerSlideCache(this.productIds, slides);
    }
  }

  applyFallback() {
    const existingCount = this.querySelectorAll('.swiper-wrapper .swiper-slide').length;
    const template = this.querySelector('template[data-cart-cross-sell-fallback]');
    if (!template?.content) {
      if (existingCount > 0) {
        this.show();
        this.initSwiper();
        return;
      }
      if (this.isCartPageLayout()) {
        this.clearAndHide();
      }
      return;
    }

    const slides = [];
    const seen = new Set();

    template.content.querySelectorAll('.swiper-slide').forEach((slide) => {
      const productId = slide.dataset.productId;
      if (!productId || seen.has(productId) || this.isProductExcluded(productId)) return;
      if (!slide.querySelector('.cart-rec-card') && !slide.querySelector('.upsell-card')) return;
      const normalized = this.normalizeSlideForLayout(slide.cloneNode(true));
      if (!normalized) return;
      seen.add(productId);
      slides.push(normalized);
    });

    if (!slides.length) {
      if (existingCount > 0) {
        this.show();
        this.initSwiper();
        return;
      }
      if (this.isCartPageLayout()) {
        this.clearAndHide();
      }
      return;
    }

    this.renderSlides(slides.slice(0, this.limit));
    if (this.isCartPageLayout()) {
      this.cacheSlidesForDrawer(
        Array.from(this.querySelectorAll('.swiper-wrapper .swiper-slide'))
      );
    }
    this.show();
    this.initSwiper();
  }

  clearAndHide() {
    this.destroySwiper();
    const wrapper = this.querySelector('.swiper-wrapper');
    if (wrapper) wrapper.innerHTML = '';
    if (this.isDrawerLayout()) {
      CartCrossSell.clearDrawerSlideCache();
      CartCrossSell.clearMergedSlidesCache();
      this.removeAttribute('data-initialized');
    }
    if (this.isCartPageLayout()) {
      this.setAttribute('hidden', '');
    }
  }

  show() {
    this.removeAttribute('hidden');
  }

  destroySwiper() {
    const el = this.querySelector('.cart-upsell__swiper');
    if (el?._swiperInstance && typeof el._swiperInstance.destroy === 'function') {
      try {
        el._swiperInstance.destroy(true, true);
      } catch (e) {
        /* ignore */
      }
    }
    if (el) el._swiperInstance = null;
    if (window.__cartUpsellSwiper?.el === el) {
      window.__cartUpsellSwiper = null;
    }
  }

  getCartPageSlidesPerView(slideCount) {
    if (slideCount <= 1) return 1;
    if (window.matchMedia('(min-width: 1200px)').matches) return Math.min(3, slideCount);
    if (window.matchMedia('(min-width: 750px)').matches) return Math.min(2, slideCount);
    return slideCount <= 1 ? 1 : 1.15;
  }

  getSlidesPerView(el) {
    const n = el.querySelectorAll('.swiper-slide').length;
    if (this.isCartPageLayout()) {
      return this.getCartPageSlidesPerView(n);
    }
    return n <= 1 ? 1 : 1.2;
  }

  getSwiperSpaceBetween() {
    return this.isCartPageLayout() ? 20 : 14;
  }

  markSingleClass(el) {
    if (this.isCartPageLayout()) return;
    const n = el.querySelectorAll('.swiper-slide').length;
    el.classList.toggle('is-single', n <= 1);
  }

  getSwiperConfig(el) {
    const component = this;
    const isPage = this.isCartPageLayout();
    const prevEl = this.querySelector('.cart-page-cross-sell__prev');
    const nextEl = this.querySelector('.cart-page-cross-sell__next');
    const paginationEl = this.querySelector('.cart-page-cross-sell__pagination');
    const slideCount = el.querySelectorAll('.swiper-slide').length;

    const base = {
      slidesPerView: this.getSlidesPerView(el),
      spaceBetween: this.getSwiperSpaceBetween(),
      watchOverflow: true,
      observer: true,
      observeParents: true,
      resizeObserver: true,
    };

    if (isPage) {
      const wideDesktopView = Math.min(3, slideCount);
      const tabletView = Math.min(2, slideCount);
      const canSlide = slideCount > 1;
      return {
        ...base,
        slidesPerView: slideCount <= 1 ? 1 : 1.15,
        spaceBetween: 16,
        speed: 500,
        grabCursor: canSlide,
        watchSlidesProgress: true,
        slidesPerGroup: 1,
        navigation: prevEl && nextEl && canSlide ? { prevEl, nextEl } : undefined,
        pagination:
          paginationEl && canSlide
            ? { el: paginationEl, clickable: true, dynamicBullets: slideCount > 6 }
            : undefined,
        breakpoints: {
          0: { slidesPerView: slideCount <= 1 ? 1 : 1.15, spaceBetween: 12 },
          750: { slidesPerView: tabletView, spaceBetween: 16 },
          1200: { slidesPerView: wideDesktopView, spaceBetween: 20 },
        },
        on: {
          init() {
            component.updateNavState(this);
            setTimeout(() => this.update(), 100);
          },
          resize() {
            component.updateNavState(this);
            this.update();
          },
          slideChange() {
            component.updateNavState(this);
          },
        },
      };
    }

    return {
      ...base,
      breakpoints: {
        480: { slidesPerView: this.getSlidesPerView(el) },
        640: { slidesPerView: this.getSlidesPerView(el) },
        990: { slidesPerView: this.getSlidesPerView(el) },
      },
      on: {
        init() {
          setTimeout(() => this.update(), 100);
        },
        resize() {
          this.params.slidesPerView = component.getSlidesPerView(this.el);
          component.markSingleClass(this.el);
          this.update();
        },
        observerUpdate() {
          this.params.slidesPerView = component.getSlidesPerView(this.el);
          component.markSingleClass(this.el);
          this.update();
        },
      },
    };
  }

  updateNavState(swiper) {
    if (!this.isCartPageLayout()) return;
    const nav = this.querySelector('.cart-page-cross-sell__nav');
    const prev = this.querySelector('.cart-page-cross-sell__prev');
    const next = this.querySelector('.cart-page-cross-sell__next');
    if (!prev || !next) return;

    const perView =
      typeof swiper.params.slidesPerView === 'number'
        ? swiper.params.slidesPerView
        : this.getCartPageSlidesPerView(swiper.slides.length);
    const hideNav = swiper.slides.length <= perView;
    if (nav) nav.hidden = hideNav;
    const pagination = this.querySelector('.cart-page-cross-sell__pagination');
    if (pagination) pagination.hidden = hideNav;

    prev.disabled = swiper.isBeginning;
    next.disabled = swiper.isEnd;
    prev.classList.toggle('swiper-button-disabled', swiper.isBeginning);
    next.classList.toggle('swiper-button-disabled', swiper.isEnd);
  }

  waitForSwiper(maxAttempts = 40, intervalMs = 50) {
    return new Promise((resolve) => {
      let attempts = 0;
      const tick = () => {
        if (window.Swiper) {
          resolve(true);
          return;
        }
        attempts += 1;
        if (attempts >= maxAttempts) {
          resolve(false);
          return;
        }
        setTimeout(tick, intervalMs);
      };
      tick();
    });
  }

  async initSwiper() {
    const el = this.querySelector('.cart-upsell__swiper');
    if (!el || !el.querySelector('.swiper-slide')) return;

    const hasSwiper = await this.waitForSwiper();
    if (!hasSwiper) return;

    this.markSingleClass(el);

    if (el._swiperInstance && !el._swiperInstance.destroyed) {
      el._swiperInstance.params.slidesPerView = this.getSlidesPerView(el);
      el._swiperInstance.update();
      if (el._swiperInstance.navigation) {
        this.updateNavState(el._swiperInstance);
      }
      return;
    }

    this.destroySwiper();

    const instance = new Swiper(el, this.getSwiperConfig(el));

    el._swiperInstance = instance;
    if (!this.isCartPageLayout()) {
      window.__cartUpsellSwiper = instance;
    }

    requestAnimationFrame(() => {
      try {
        instance.update();
        this.updateNavState(instance);
      } catch (e) {
        /* ignore */
      }
    });
  }

  observeDrawerUpdates() {
    if (!this.isDrawerLayout()) return;

    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    this.mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'childList') continue;
        const crossSell = drawer.querySelector('cart-cross-sell');
        if (crossSell && crossSell !== this && !crossSell.dataset.initialized) {
          return;
        }
        if (mutation.target.contains?.(this) || mutation.target === this) {
          setTimeout(() => this.initSwiper(), CartCrossSell.INIT_DELAY);
          break;
        }
      }
    });

    this.mutationObserver.observe(drawer, { childList: true, subtree: true });
  }

  getCartAddUrl() {
    const routes = window.routes || {};
    const base = routes.cart_add_url || '/cart/add';
    return base.endsWith('.js') ? base : `${base}.js`;
  }

  getCartSectionIds() {
    const ids = [];
    const cartItemsSection = document.getElementById('main-cart-items');
    const cartFooterSection = document.getElementById('main-cart-footer');
    if (cartItemsSection?.dataset?.id) ids.push(cartItemsSection.dataset.id);
    if (cartFooterSection?.dataset?.id) ids.push(cartFooterSection.dataset.id);
    ids.push('cart-icon-bubble');
    return ids;
  }

  applySectionsFromResponse(sections) {
    const cartItems = document.querySelector('cart-items');
    if (!cartItems || !sections || typeof cartItems.getSectionsToRender !== 'function') {
      return false;
    }

    cartItems.getSectionsToRender().forEach((section) => {
      const html = sections[section.section];
      if (!html) return;

      const root = document.getElementById(section.id);
      if (!root) return;

      const elementToReplace = root.querySelector(section.selector) || root;
      if (typeof cartItems.getSectionInnerHTML === 'function') {
        elementToReplace.innerHTML = cartItems.getSectionInnerHTML(html, section.selector);
      }
    });

    return true;
  }

  async refreshCartPage() {
    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.onCartUpdate === 'function') {
      await cartItems.onCartUpdate();
      return;
    }

    const routes = window.routes || {};
    const cartUrl = routes.cart_url || '/cart';
    const sectionId = document.getElementById('main-cart-items')?.dataset?.id || 'main-cart-items';

    try {
      const html = await fetch(`${cartUrl}?section_id=${sectionId}`).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const source = doc.querySelector('cart-items');
      const target = document.querySelector('cart-items');
      if (source && target) {
        target.innerHTML = source.innerHTML;
      }
    } catch (e) {
      console.error(e);
    }
  }

  bindAddToCart() {
    if (document.documentElement.dataset.cartCrossSellBound) return;
    document.documentElement.dataset.cartCrossSellBound = 'true';

    let adding = false;

    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('.cart-upsell__add[data-variant-id]');
      if (!btn || adding) return;

      const id = btn.getAttribute('data-variant-id');
      if (!id) return;

      const crossSellRoot = btn.closest('cart-cross-sell');
      if (!crossSellRoot) return;

      e.preventDefault();
      e.stopPropagation();

      const spinner = btn.querySelector('.loading__spinner');
      const label = btn.querySelector('.cart-rec-card__btn-label');
      const slide = btn.closest('.swiper-slide');
      const productId =
        slide?.dataset?.productId || null;

      try {
        adding = true;
        btn.disabled = true;
        btn.classList.add('loading');
        if (spinner) spinner.classList.remove('hidden');
        if (label) label.style.opacity = '0';

        const payload = {
          id: Number(id),
          quantity: 1,
        };

        if (crossSellRoot.isCartPageLayout()) {
          const sections = crossSellRoot.getCartSectionIds();
          if (sections.length) {
            payload.sections = sections.join(',');
            payload.sections_url = window.location.pathname;
          }
        } else {
          payload.sections = 'cart-drawer,cart-icon-bubble';
          payload.sections_url = window.location.pathname;
        }

        const response = await fetch(crossSellRoot.getCartAddUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Add to cart failed');
        }

        const addedProductId =
          productId || (data.product_id != null ? String(data.product_id) : null);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-cross-sell',
            cartData: data,
          });
        }

        if (crossSellRoot.isCartPageLayout()) {
          if (addedProductId) {
            crossSellRoot.removeAddedProduct(addedProductId);
          }
          if (!data.sections || !crossSellRoot.applySectionsFromResponse(data.sections)) {
            await crossSellRoot.refreshCartPage();
          }
          await crossSellRoot.handleCartUpdatePage();
        } else {
          if (addedProductId) {
            CartCrossSell.removeProductFromDrawer(addedProductId);
          }
          const drawerItems = document.querySelector('cart-drawer-items');
          if (drawerItems && typeof drawerItems.onCartUpdate === 'function') {
            await drawerItems.onCartUpdate();
          }
          window.CartCrossSell?.refreshDrawerCrossSell?.();
        }
      } catch (err) {
        console.error(err);
      } finally {
        adding = false;
        btn.disabled = false;
        btn.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');
        if (label) label.style.opacity = '';
      }
    });
  }
}

if (!customElements.get('cart-cross-sell')) {
  customElements.define('cart-cross-sell', CartCrossSell);
}

window.CartCrossSell = CartCrossSell;
